import { useIntl } from 'react-intl';

import { useIsFocused } from '@sg/garnish';
import { NotificationCategoryType } from '@sg/shared/features/notifications';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { categoryMessages, messages } from './useNotificationsContent.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useNotificationsContent = (): MappedFields => {
  const isScreenFocused = useIsFocused();

  const { data } = useContentfulContentTypeEntry<Fields>({
    contentType: 'notifications',
    pause: !isScreenFocused,
  });

  // ───────────────────────────────────────────────────────────────────────

  const maybeFields = data?.fields;
  const fallbacks = useNotificationsContentFallbacks();

  return {
    //
    // ─── General ─────────────────────────────────────────────────────────

    title: maybeFields?.title ?? fallbacks.title,
    pushNotificationOptionLabel:
      maybeFields?.pushNotificationOptionLabel ?? fallbacks.pushNotificationOptionLabel,
    emailOptionLabel: maybeFields?.emailOptionLabel ?? fallbacks.emailOptionLabel,
    pushNotificationsPermissionMessage:
      maybeFields?.pushNotificationsPermissionMessage ??
      fallbacks.pushNotificationsPermissionMessage,
    goToDeviceSettingsButtonLabel:
      maybeFields?.goToDeviceSettingsButtonLabel ?? fallbacks.goToDeviceSettingsButtonLabel,
    failedToUpdatePreferencesMessage:
      maybeFields?.failedToUpdatePreferencesMessage ?? fallbacks.failedToUpdatePreferencesMessage,
    tryAgainButtonLabel: maybeFields?.tryAgainButtonLabel ?? fallbacks.tryAgainButtonLabel,
    unresolvedCategoryMessage:
      maybeFields?.unresolvedCategoryMessage ?? fallbacks.unresolvedCategoryMessage,

    // ─── Categories ──────────────────────────────────────────────────────

    categories: {
      [NotificationCategoryType.OrderStatus]: {
        title:
          maybeFields?.orderStatusCategoryTitle ??
          fallbacks.categories[NotificationCategoryType.OrderStatus].title,
        subtitle:
          maybeFields?.orderStatusCategorySubtitle ??
          fallbacks.categories[NotificationCategoryType.OrderStatus].subtitle,
      },
      [NotificationCategoryType.Marketing]: {
        title:
          maybeFields?.marketingCategoryTitle ??
          fallbacks.categories[NotificationCategoryType.Marketing].title,
        subtitle:
          maybeFields?.marketingCategorySubtitle ??
          fallbacks.categories[NotificationCategoryType.Marketing].subtitle,
      },
    },
  };
};

// ─────────────────────────────────────────────────────────────────────────────

const useNotificationsContentFallbacks = (): MappedFields => {
  const { formatMessage } = useIntl();

  const maybeOrderStatusCategoryTitle =
    categoryMessages[NotificationCategoryType.OrderStatus].title;
  const maybeOrderStatusCategorySubtitle =
    categoryMessages[NotificationCategoryType.OrderStatus].subtitle;
  const maybeMarketingCategoryTitle = categoryMessages[NotificationCategoryType.Marketing].title;
  const maybeMarketingCategorySubtitle =
    categoryMessages[NotificationCategoryType.Marketing].subtitle;

  return {
    //
    // ─── General ─────────────────────────────────────────────────────────

    title: formatMessage(messages.title),
    pushNotificationOptionLabel: formatMessage(messages.pushNotificationsLabel),
    emailOptionLabel: formatMessage(messages.emailLabel),
    pushNotificationsPermissionMessage: formatMessage(messages.pushDisabledNotice), // prettier-ignore
    goToDeviceSettingsButtonLabel: formatMessage(messages.goToDeviceSettings),
    failedToUpdatePreferencesMessage: formatMessage(messages.preferencesUpdateFailure), // prettier-ignore
    tryAgainButtonLabel: formatMessage(messages.tryAgain),

    unresolvedCategoryMessage: undefined,

    // ─────────────────────────────────────────────────────────────────────

    categories: {
      [NotificationCategoryType.OrderStatus]: {
        title: maybeOrderStatusCategoryTitle
          ? formatMessage(maybeOrderStatusCategoryTitle)
          : undefined,
        subtitle: maybeOrderStatusCategorySubtitle
          ? formatMessage(maybeOrderStatusCategorySubtitle)
          : undefined,
      },
      [NotificationCategoryType.Marketing]: {
        title: maybeMarketingCategoryTitle ? formatMessage(maybeMarketingCategoryTitle) : undefined,
        subtitle: maybeMarketingCategorySubtitle
          ? formatMessage(maybeMarketingCategorySubtitle)
          : undefined,
      },
    },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Fields = {
  title: string | undefined;
  pushNotificationOptionLabel: string | undefined;
  emailOptionLabel: string | undefined;
  pushNotificationsPermissionMessage: string | undefined;
  goToDeviceSettingsButtonLabel: string | undefined;
  failedToUpdatePreferencesMessage: string | undefined;
  tryAgainButtonLabel: string | undefined;
  orderStatusCategoryTitle: string | undefined;
  orderStatusCategorySubtitle: string | undefined;
  marketingCategoryTitle: string | undefined;
  marketingCategorySubtitle: string | undefined;
  unresolvedCategoryMessage: string | undefined;
};

type MappedFields = {
  title: string;
  pushNotificationOptionLabel: string;
  emailOptionLabel: string;
  pushNotificationsPermissionMessage: string;
  goToDeviceSettingsButtonLabel: string;
  failedToUpdatePreferencesMessage: string;
  tryAgainButtonLabel: string;
  unresolvedCategoryMessage: string | undefined;

  categories: Record<
    NotificationCategoryType,
    { title: string | undefined; subtitle: string | undefined }
  >;
};
